import { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Card, message, Space, Spin, Tag, Typography } from 'antd'
import {
  AppleOutlined,
  ExperimentOutlined,
  FireOutlined,
} from '@ant-design/icons'
import * as drvApi from '@/api/drivers'
import type { BrowserDriver } from '@/api/drivers'
import '../../styles/aurora-page.css'

const { Title, Text } = Typography

/** 内核图标 */
const BROWSER_ICONS: Record<string, React.ReactNode> = {
  chromium: <ExperimentOutlined />,
  firefox: <FireOutlined />,
  webkit: <AppleOutlined />,
}

/** driver_type 展示文案 */
const TYPE_LABELS: Record<string, string> = {
  system: '系统浏览器',
  playwright: 'Playwright 托管',
}

/** 引擎当前支持的内核（Firefox / WebKit 待开发，见页内提示） */
const SUPPORTED_KERNELS = ['chromium']

export default function Drivers() {
  const [drivers, setDrivers] = useState<BrowserDriver[]>([])
  const [loading, setLoading] = useState(false)
  const [acting, setActing] = useState<string | null>(null)

  const loadDrivers = useCallback(async () => {
    setLoading(true)
    try {
      setDrivers(await drvApi.listDrivers())
    } finally {
      setLoading(false)
    }
  }, [])

  // 进入页面一次性查询；存在安装中的浏览器时每 3 秒轮询进度，完成后停止并提示结果
  useEffect(() => {
    loadDrivers()
  }, [loadDrivers])

  useEffect(() => {
    const installing = drivers.some((d) => d.install_status === 'installing')
    if (!installing) return
    const timer = window.setInterval(async () => {
      const list = await drvApi.listDrivers()
      setDrivers(list)
      if (!list.some((d) => d.install_status === 'installing')) {
        for (const d of list) {
          if (d.install_status === 'success') message.success(`${d.browser_name} 驱动安装成功`)
          if (d.install_status === 'failed') message.error(`${d.browser_name} 驱动安装失败：${d.install_message}`)
        }
      }
    }, 3000)
    return () => window.clearInterval(timer)
  }, [drivers])

  const handleInstall = async (browserCode: string) => {
    setActing(browserCode)
    try {
      const res = await drvApi.installDriver(browserCode)
      if (res.accepted) {
        message.success('安装任务已开始')
        await loadDrivers()
      } else {
        message.warning('该浏览器已在安装中')
      }
    } catch {
      message.error('安装任务下发失败')
    } finally {
      setActing(null)
    }
  }

  const handleSetDefault = async (browserCode: string) => {
    setActing(browserCode)
    try {
      const res = await drvApi.setDefaultDriver(browserCode)
      message.success(`已将 ${res.browser_name} 设为默认浏览器`)
      await loadDrivers()
    } catch {
      message.error('设置默认浏览器失败（需已安装该浏览器驱动）')
    } finally {
      setActing(null)
    }
  }

  return (
    <div className="aurora-page">
      <div className="page-header">
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>驱动管理</Title>
        <Space>
          <Button onClick={loadDrivers}>刷新</Button>
        </Space>
      </div>

      <div className="content-card" style={{ padding: 20 }}>
        <Spin spinning={loading && drivers.length === 0}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 14, borderRadius: 8 }}
            message="当前引擎仅支持 Chromium 内核"
            description="Firefox / WebKit 相关功能待开发，可先安装驱动以备后续启用。"
          />
          <Space direction="vertical" size={14} style={{ width: '100%' }}>
            {drivers.map((d) => {
              const installing = d.install_status === 'installing'
              return (
                <Card
                  key={d.browser_code}
                  size="small"
                  style={{ borderRadius: 10, border: '1px solid var(--au-border)' }}
                  styles={{ body: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' } }}
                >
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--au-primary-bg, rgba(0,0,0,0.04))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: 'var(--au-primary)' }}>
                    {BROWSER_ICONS[d.browser_code] || <ExperimentOutlined />}
                  </div>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <Space size={10}>
                      <Text strong style={{ fontSize: 14 }}>{d.browser_name}</Text>
                      <Tag style={{ margin: 0 }}>{TYPE_LABELS[d.driver_type] || d.driver_type}</Tag>
                      <Tag color={d.installed ? 'success' : 'error'} style={{ margin: 0 }}>
                        {d.installed ? '已安装' : '未安装'}
                      </Tag>
                      {d.is_default && <Tag color="processing" style={{ margin: 0 }}>默认浏览器</Tag>}
                      {installing && <Tag color="warning" style={{ margin: 0 }}>安装中...</Tag>}
                    </Space>
                    <div>
                      <Text style={{ fontSize: 12, color: 'var(--au-text-faint)' }}>
                        {TYPE_LABELS[d.driver_type] || d.driver_type}驱动
                      </Text>
                    </div>
                    {d.path && (
                      <div style={{ marginTop: 4 }}>
                        <Text code style={{ fontSize: 12 }}>{d.path}</Text>
                      </div>
                    )}
                    {d.install_status === 'failed' && d.install_message && (
                      <div style={{ marginTop: 6 }}>
                        <Alert type="error" showIcon style={{ padding: '4px 10px' }} message={d.install_message} />
                      </div>
                    )}
                  </div>
                  <Space>
                    {!d.installed && (
                      <Button type="primary" loading={installing || acting === d.browser_code} onClick={() => handleInstall(d.browser_code)}>
                        一键安装
                      </Button>
                    )}
                    {d.installed && !d.is_default && SUPPORTED_KERNELS.includes(d.browser_code) && (
                      <Button loading={acting === d.browser_code} onClick={() => handleSetDefault(d.browser_code)}>
                        设为默认
                      </Button>
                    )}
                  </Space>
                </Card>
              )
            })}
          </Space>
          <div style={{ marginTop: 14 }}>
            <Text style={{ fontSize: 12, color: 'var(--au-text-faint)' }}>
              内核状态实时探测本机 Playwright 缓存目录（安装一次全局可用，容器镜像构建时已预装 Chromium）；一键安装通过 Playwright 官方安装器进行。引擎启动时使用「默认内核」，切换后重启引擎生效。
            </Text>
          </div>
        </Spin>
      </div>
    </div>
  )
}
